import React from 'react';

/*
 * Procedural planet surfaces.
 *
 * Each archetype is an feTurbulence recipe composited over the body's own
 * colour: a soft-light pass for the broad terrain and a lighter overlay pass
 * for highlights. Noise is generated in a fixed 100x100 tile which is then
 * scaled to the body, so one filter set serves a 5px satellite and a 54px core
 * at the same apparent grain.
 *
 * No image assets: the whole library is a few hundred bytes of filter.
 */

export const SURFACES = {
  // structured horizontal layering -- geological, deliberate
  strata: { type: 'fractalNoise', freq: '0.010 0.11', octaves: 4, seed: 5, slope: 1.15, intercept: -0.12, overlay: 0.45 },
  // gas-giant belts
  bands: { type: 'fractalNoise', freq: '0.012 0.075', octaves: 4, seed: 7, slope: 1.25, intercept: -0.15, overlay: 0.45 },
  // crystalline fracture
  ice: { type: 'turbulence', freq: '0.05 0.03', octaves: 3, seed: 13, slope: 1.0, intercept: -0.1, overlay: 0.4 },
  // dense mottled rock
  rock: { type: 'fractalNoise', freq: '0.085', octaves: 5, seed: 3, slope: 1.0, intercept: -0.12, overlay: 0.34 },
  // water with landmasses
  ocean: { type: 'fractalNoise', freq: '0.030', octaves: 3, seed: 21, slope: 1.9, intercept: -0.52, overlay: 0.3 },
  // fine wind-blown grain
  dust: { type: 'fractalNoise', freq: '0.14', octaves: 3, seed: 17, slope: 0.9, intercept: -0.08, overlay: 0.45 },
  // soft atmosphere
  cloud: { type: 'fractalNoise', freq: '0.034', octaves: 4, seed: 29, slope: 1.25, intercept: -0.18, overlay: 0.45 },
  // veined
  marble: { type: 'turbulence', freq: '0.016 0.038', octaves: 5, seed: 11, slope: 1.05, intercept: -0.1, overlay: 0.45 },
  // cracked and glowing
  molten: { type: 'turbulence', freq: '0.035', octaves: 4, seed: 31, slope: 1.3, intercept: -0.2, overlay: 0.45 },
};

/** Which world each hat gets. Chosen so neighbours in the orrery differ. */
export const HAT_SURFACE = {
  arch: 'strata',
  devops: 'bands',
  sre: 'ice',
  sec: 'rock',
  dev: 'ocean',
  test: 'dust',
  ux: 'cloud',
  design: 'marble',
  ai: 'molten',
};

export const SURFACE_CYCLE = ['bands', 'strata', 'marble', 'ice', 'rock', 'cloud', 'dust', 'ocean', 'molten'];

/*
 * Axial tilt per hat. The turbulence that makes belts and strata is
 * anisotropic and axis-aligned, so without an obliquity every world would be
 * banded on exactly the same axis -- which reads as nine copies rather than
 * nine planets.
 */
export const HAT_TILT = {
  arch: -8,
  devops: 14,
  sre: -33,
  sec: 6,
  dev: 24,
  test: -19,
  ux: 41,
  design: -52,
  ai: 29,
};

/** Spread of tilts for anything cycling through surfaces, e.g. career chapters. */
export const TILT_CYCLE = [-12, 27, -38, 9, 48, -25, 17, -44, 34];

export const SurfaceDefs = ({ uid }) => (
  <>
    {Object.entries(SURFACES).map(([name, s]) => (
      <filter key={name} id={`surf-${name}-${uid}`} x="0" y="0" width="100%" height="100%">
        <feTurbulence
          type={s.type}
          baseFrequency={s.freq}
          numOctaves={s.octaves}
          seed={s.seed}
          result="n"
        />
        <feColorMatrix in="n" type="saturate" values="0" />
        <feComponentTransfer>
          <feFuncA type="linear" slope={s.slope} intercept={s.intercept} />
        </feComponentTransfer>
      </filter>
    ))}
  </>
);

/**
 * The terrain of one body: base colour plus two noise passes.
 * Must be rendered inside a clipPath that matches the body.
 */
const PlanetSurface = ({ uid, cx, cy, r, color, surface = 'rock', tilt = 0 }) => {
  const spec = SURFACES[surface] ?? SURFACES.rock;
  const scale = (r * 2) / 100;
  // Rotating the tile about the body's centre is safe at any angle: the square
  // tile's apothem equals r, so the clipped circle stays fully covered.
  const tile = (blend, opacity) => (
    <g
      transform={`rotate(${tilt} ${cx} ${cy}) translate(${cx - r} ${cy - r}) scale(${scale})`}
      style={{ mixBlendMode: blend }}
      opacity={opacity}
    >
      <rect width="100" height="100" filter={`url(#surf-${surface}-${uid})`} />
    </g>
  );

  return (
    // Isolated so the blend modes see only this body, not the page behind it.
    <g style={{ isolation: 'isolate' }}>
      <circle cx={cx} cy={cy} r={r} fill={color} />
      {tile('soft-light', 1)}
      {tile('overlay', spec.overlay)}
    </g>
  );
};

export default PlanetSurface;
